<template>
  <div
    class="flow-analytics-panel"
    :data-operator-id="operatorId"
    @click.stop
  >
    <!--
      Mode contact : timeline événements (sends) ou badge selon le statut
      du passage (passed / stopped / finished / paused / blocked...).
      Le libellé du header + le badge se déclinent par status pour qu'un
      filtre stoppé n'affiche pas "Étape franchie" / "Passé le …".
    -->
    <div
      v-if="isContactMode"
      class="flow-analytics-contact"
      :class="`flow-analytics-contact--${badgeVariant}`"
    >
      <div class="flow-analytics-contact__header">
        <div class="flow-analytics-contact__header-line">
          <i class="fa-regular fa-clock" />
          <span>{{ t(headerLineI18nKey, { date: formattedPassedAtDate, time: formattedPassedAtTime }) }}</span>
        </div>
      </div>
      <ul
        v-if="hasContactTimeline"
        class="flow-analytics-contact__timeline"
      >
        <li
          v-for="evt in timelineItems"
          :key="evt.key"
          class="flow-analytics-contact__item"
          :class="{ 'flow-analytics-contact__item--done': !!evt.time }"
        >
          <i
            class="flow-analytics-contact__bullet"
            :class="evt.time ? 'fa-solid fa-circle' : 'fa-regular fa-circle'"
          />
          <span class="flow-analytics-contact__label">
            {{ t(`workflowStats.chip.events.${evt.key}`) }}
            <span
              v-if="evt.count && evt.count > 1"
              class="flow-analytics-contact__count"
            >
              ({{ t('workflowStats.chip.countTimes', { count: evt.count }) }})
            </span>
          </span>
          <span class="flow-analytics-contact__time">
            {{ evt.time ? formatDateTime(evt.time) : '—' }}
          </span>
        </li>
      </ul>
      <component
        :is="opensSupport ? 'button' : 'div'"
        v-else
        class="flow-analytics-contact__badge"
        :class="[
          `flow-analytics-contact__badge--${badgeVariant}`,
          { 'flow-analytics-contact__badge--action': opensSupport },
        ]"
        :type="opensSupport ? 'button' : null"
        @click="opensSupport ? openSupport() : null"
      >
        <i :class="badgeIcon" />
        <span>{{ t(badgeI18nKey) }}</span>
      </component>
    </div>

    <!-- Mode global : lecture adaptée à la famille de la box -->
    <div
      v-else-if="hasGlobalStats || hasSendStats"
      class="flow-analytics-figures"
    >
      <!-- Entonnoir du message (envoi / affichage on-site) -->
      <div
        v-if="presentation.funnel.length"
        class="flow-analytics-funnel"
        :style="funnelStyle"
      >
        <div
          v-for="stage in presentation.funnel"
          :key="stage.labelKey"
          class="flow-analytics-funnel__cell"
        >
          <strong>{{ formatValue(stage.value) }}</strong>
          <span>{{ t(stage.labelKey) }}</span>
          <small
            v-if="stage.rate !== undefined"
            :class="{ 'flow-analytics-rate--zero': stage.rate === 0 }"
          >{{ formatRate(stage.rate) }}</small>
        </div>
      </div>

      <div
        v-if="revenue"
        class="flow-analytics-revenue"
      >
        <strong v-if="revenue.amount">{{ revenue.amount }}</strong>
        <span>{{ t('campaigns.common.headers.sales') }} : {{ formatValue(revenue.orders) }}</span>
      </div>

      <!-- Chiffre principal de la famille -->
      <div
        v-if="presentation.headline"
        class="flow-analytics-headline"
      >
        <strong>{{ formatValue(presentation.headline.value) }}</strong>
        <span>{{ t(presentation.headline.labelKey) }}</span>
        <em
          v-if="presentation.headline.rate !== undefined"
          :class="{ 'flow-analytics-rate--zero': presentation.headline.rate === 0 }"
        >{{ formatRate(presentation.headline.rate) }}</em>
      </div>

      <!-- Répartition : la somme des segments égale toujours le total entré -->
      <div
        v-if="splitSegments.length > 1"
        class="flow-analytics-split"
      >
        <i
          v-for="segment in splitSegments"
          :key="segment.tone"
          :class="`flow-analytics-split__part flow-analytics-split__part--${segment.tone}`"
          :style="{ flexGrow: segment.value }"
        />
      </div>

      <div
        v-if="presentation.breakdown.length"
        class="flow-analytics-stats"
      >
        <component
          :is="item.action === 'support' ? 'button' : 'div'"
          v-for="(item, index) in presentation.breakdown"
          :key="item.labelKey"
          class="flow-analytics-stat"
          :class="{
            'flow-analytics-stat--new-group': isGroupStart(index),
            'flow-analytics-stat--action': item.action === 'support',
          }"
          :type="item.action === 'support' ? 'button' : null"
          @click="item.action === 'support' ? openSupport() : null"
        >
          <span class="flow-analytics-stat__label">
            <i :class="`flow-analytics-stat__dot flow-analytics-stat__dot--${item.tone}`" />
            {{ t(item.labelKey) }}
          </span>
          <span class="flow-analytics-stat__value">
            <strong>{{ formatValue(item.value) }}</strong>
          </span>
        </component>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  computed, defineComponent, PropType,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Gleap from 'gleap';
import { buildOperatorPresentation } from '@/composables/flows/analyticsPresentation';
import { formatNumberToCurrency } from '@/helpers/Number';
import { UserState } from '@/composables/User';
import type {
  ContactEvents,
  GlobalStatistics,
  OperatorStepStatus,
  SendStatistics,
} from '@/composables/flows/useFlowAnalytics';

type ContactEventKey = 'received' | 'opened' | 'clicked' | 'ordered';

interface TimelineItem {
  key: ContactEventKey;
  time?: string;
  count?: number;
}

export default defineComponent({
  name: 'FlowAnalyticsChip',

  props: {
    operatorId: {
      type: String,
      required: true,
    },

    /** Type de box (ex. boxsendemail) : détermine la famille de lecture. */
    operatorType: {
      type: String,
      default: '',
    },

    /** Une box en tête de scénario compte des entrées, pas une action attendue. */
    isRoot: {
      type: Boolean,
      default: false,
    },

    /** Événement du déclencheur générique, pour choisir le bon verbe. */
    triggerEvent: {
      type: String,
      default: '',
    },

    globalStats: {
      type: Object as PropType<GlobalStatistics | null>,
      default: null,
    },

    sendStats: {
      type: Object as PropType<SendStatistics | null>,
      default: null,
    },

    /**
     * Mode contact uniquement : ISO 8601 de l'entrée du contact dans cette
     * étape. Sa présence bascule le rendu en mode contact (timeline + badge).
     */
    passedAt: {
      type: String as PropType<string | null>,
      default: null,
    },

    /**
     * Mode contact uniquement : ISO 8601 de la sortie du contact. Utilisé
     * pour les libellés "Bloqué le ..." / "Validé le ..." (instant exact
     * où le worker a tranché pour les boxes filtres).
     */
    exitedAt: {
      type: String as PropType<string | null>,
      default: null,
    },

    /**
     * Mode contact uniquement : statut sémantique du passage du contact.
     * Pilote le libellé/couleur du header + badge.
     */
    stepStatus: {
      type: String as PropType<OperatorStepStatus | null>,
      default: null,
    },

    /**
     * Mode contact + step d'envoi uniquement : événements horodatés du contact.
     */
    contactEvents: {
      type: Object as PropType<ContactEvents | null>,
      default: null,
    },
  },

  setup(props) {
    const { t, locale } = useI18n();

    const hasGlobalStats = computed(() => !!props.globalStats);
    const hasSendStats = computed(() => !!props.sendStats);

    /**
     * Lecture de la box, dérivée des seuls compteurs déjà chargés : aucun appel
     * supplémentaire, et des libellés qui ne dépendent jamais du contenu d'un
     * filtre (voir analyticsPresentation).
     */
    const presentation = computed(() => buildOperatorPresentation({
      operatorType: props.operatorType,
      isRoot: props.isRoot,
      event: props.triggerEvent || undefined,
      global: props.globalStats,
      send: props.sendStats,
    }));

    /**
     * Vrai sur la première ligne d'un groupe autre que le précédent : sert à
     * poser un filet entre « n'ont pas reçu » et « après réception ».
     */
    const isGroupStart = (index: number): boolean => {
      const lines = presentation.value.breakdown;
      return index > 0 && lines[index].group !== lines[index - 1].group;
    };

    /**
     * Colonnes de l'entonnoir : une par étape, mais deux rangées de deux au-delà
     * de trois, pour ne pas comprimer les libellés sur la largeur d'une box.
     */
    const funnelStyle = computed(() => {
      const count = presentation.value.funnel.length;
      const columns = count > 3 ? 2 : Math.max(count, 1);
      return { gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` };
    });

    /**
     * Bloc revenus : masqué tant qu'aucune commande n'est attribuée. Le montant
     * arrive déjà formaté par l'API avec la devise de la boutique.
     */
    const revenue = computed(() => {
      const value = presentation.value.revenue;
      if (!value || (!value.orders && !value.amount)) {
        return null;
      }
      // L'éditeur reçoit un montant déjà mis en forme avec la devise ; l'API
      // publique renvoie un nombre brut, qu'on met en forme ici.
      const { amount } = value;
      const label = typeof amount === 'number'
        ? formatNumberToCurrency(amount, UserState.activeShop?.currency ?? 'EUR')
        : amount;
      return { orders: value.orders, amount: label };
    });

    /**
     * Segments de la barre de répartition : ce qui a continué, puis chaque motif
     * d'arrêt. La somme vaut toujours le total arrivé sur la box.
     */
    const splitSegments = computed(() => {
      const {
        family, headline, breakdown, splitBase,
      } = presentation.value;
      const first = splitBase ?? headline?.value ?? 0;
      // Une attente en cours n'est pas un aboutissement : la teinter en vert la
      // rendrait indiscernable de la ligne « ont terminé l’attente ».
      const firstTone = family === 'wait' ? 'wait' : 'go';
      return [
        { tone: firstTone, value: first },
        ...breakdown
          .filter((item) => item.group !== 'after')
          .map((item) => ({ tone: item.tone, value: item.value })),
      ].filter((segment) => segment.value > 0);
    });

    /** Taux affiché avec le séparateur décimal de la langue active. */
    const formatRate = (value: number): string => {
      const rounded = value < 1 && value > 0 ? value.toFixed(1) : String(Math.round(value));
      return `${rounded.replace('.', locale.value.startsWith('fr') ? ',' : '.')} %`;
    };

    /**
     * Nombre mis en forme dans la langue de l'interface, et non dans celle du
     * navigateur : un compte en français sur un navigateur anglais lisait
     * « 4,120 » au milieu de libellés français.
     */
    const formatValue = (value: number | string): string => {
      if (typeof value === 'string') return value;
      return value.toLocaleString(locale.value);
    };

    // ── Mode contact ───────────────────────────────────────────────────────
    const isContactMode = computed(() => !!props.passedAt);

    const hasContactTimeline = computed(
      () => !!props.contactEvents
        && Object.values(props.contactEvents).some((v) => !!v),
    );

    const timelineItems = computed<TimelineItem[]>(() => {
      const evts = props.contactEvents ?? {};
      return [
        { key: 'received', time: evts.receivedAt },
        { key: 'opened', time: evts.openedAt, count: evts.openCount },
        { key: 'clicked', time: evts.clickedAt, count: evts.clickCount },
        { key: 'ordered', time: evts.orderedAt, count: evts.orderCount },
      ];
    });

    // Locale i18n active (fr / en / ...) — utilisée pour formater date et
    // heure selon la langue du user : 24h naturellement en français, AM/PM en
    // anglais, etc. Aucun replace hardcodé.
    const formatDate = (iso?: string | null): string => {
      if (!iso) return '';
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return '';
      return d.toLocaleDateString(locale.value, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    };

    const formatTime = (iso?: string | null): string => {
      if (!iso) return '';
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return '';
      return d.toLocaleTimeString(locale.value, {
        hour: '2-digit',
        minute: '2-digit',
      });
    };

    /**
     * Format compact « DD/MM HH:MM » pour la timeline événements — peut
     * couvrir des jours différents que le passedAt (ouverture/clic peuvent
     * arriver plusieurs jours après l'envoi).
     */
    const formatDateTime = (iso?: string | null): string => {
      if (!iso) return '';
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return '';
      const date = d.toLocaleDateString(locale.value, {
        day: '2-digit',
        month: '2-digit',
      });
      const time = d.toLocaleTimeString(locale.value, {
        hour: '2-digit',
        minute: '2-digit',
      });
      return `${date} ${time}`;
    };

    // Pour le header + badge en mode contact on préfère afficher
    // l'instant de sortie (exitedAt) plutôt que l'entrée — c'est lui qui
    // est sémantiquement pertinent pour "Bloqué le …" / "Validé le …".
    // Fallback sur passedAt si exitedAt n'est pas fourni.
    const chipReferenceDate = computed(
      () => props.exitedAt ?? props.passedAt,
    );
    const formattedPassedAtDate = computed(() => formatDate(chipReferenceDate.value));
    const formattedPassedAtTime = computed(() => formatTime(chipReferenceDate.value));

    // Clé i18n du header line. Suffixée par stepStatus quand disponible
    // (passed/stopped/finished/paused/blockedByMarketingPressure/
    // blockNoCreditsSms), fallback `default` (= ancien libellé "Passé le …").
    const headerLineI18nKey = computed(() => {
      const status = props.stepStatus;
      return status
        ? `workflowStats.chip.headerLine.${status}`
        : 'workflowStats.chip.headerLine.default';
    });

    // Idem pour le badge final ("Étape franchie" / "Filtre non validé" / …).
    const badgeI18nKey = computed(() => {
      const status = props.stepStatus;
      return status
        ? `workflowStats.chip.badge.${status}`
        : 'workflowStats.chip.badge.default';
    });

    // Mapping stepStatus → variante visuelle CSS (success/danger/warning/info).
    // Sert à colorer le badge ET le header line de manière cohérente.
    const badgeVariant = computed<'success' | 'danger' | 'warning' | 'info'>(() => {
      switch (props.stepStatus) {
        case 'stopped':
          return 'danger';
        case 'paused':
          return 'warning';
        case 'finished':
          return 'info';
        case 'blockedByMarketingPressure':
        case 'blockNoCreditsSms':
        case 'blockedByPlanLimit':
        case 'campaignInactive':
        case 'blockedByDeliverability':
        case 'blockedByPersistence':
        case 'blockedByMaxDelay':
        case 'outOfDateRange':
        case 'campaignDisabled':
          return 'warning';
        // Un panier déjà traité est une fin normale, pas un incident.
        case 'cartAlreadyProcessed':
          return 'info';
        // Cause non enregistrée par le moteur : seul le support peut trancher.
        case 'internalError':
          return 'danger';
        case 'passed':
        default:
          return 'success';
      }
    });

    // Icône Font Awesome dérivée du status — cohérente avec le badge.
    const badgeIcon = computed(() => {
      switch (props.stepStatus) {
        case 'stopped':
          return 'fa-solid fa-circle-xmark';
        case 'paused':
          return 'fa-solid fa-hourglass-half';
        case 'finished':
          return 'fa-solid fa-flag-checkered';
        case 'blockedByMarketingPressure':
        case 'blockNoCreditsSms':
        case 'blockedByPlanLimit':
        case 'campaignInactive':
        case 'blockedByDeliverability':
        case 'blockedByPersistence':
        case 'outOfDateRange':
        case 'campaignDisabled':
          return 'fa-solid fa-ban';
        case 'blockedByMaxDelay':
          return 'fa-solid fa-hourglass-end';
        case 'cartAlreadyProcessed':
          return 'fa-solid fa-cart-shopping';
        case 'internalError':
          return 'fa-solid fa-circle-question';
        case 'passed':
        default:
          return 'fa-solid fa-check';
      }
    });

    /**
     * Seule la cause inconnue mène au support : le marchand n'a rien à corriger
     * lui-même, et le code interne du moteur ne lui dirait rien.
     */
    const opensSupport = computed(() => props.stepStatus === 'internalError');

    /** Ouvre le widget d'assistance, avec repli sur un courriel s'il est absent. */
    const openSupport = () => {
      try {
        Gleap.open();
      } catch {
        window.open('mailto:contact@shopimind.com', '_blank');
      }
    };

    return {
      t,
      hasGlobalStats,
      hasSendStats,
      presentation,
      isGroupStart,
      funnelStyle,
      revenue,
      splitSegments,
      formatRate,
      formatValue,
      isContactMode,
      hasContactTimeline,
      timelineItems,
      headerLineI18nKey,
      badgeI18nKey,
      badgeVariant,
      badgeIcon,
      opensSupport,
      openSupport,
      formattedPassedAtDate,
      formattedPassedAtTime,
      formatDateTime,
    };
  },
});
</script>

<style lang="scss" scoped>
.flow-analytics-panel {
  margin-top: 0.5rem;
  border-top: 1px solid #e2e8f0;
}

// ─── Mode contact (parcours d'un seul utilisateur) ─────────────────────────
.flow-analytics-contact {
  padding: 0.5rem 0.75rem 0.6rem;
  background: #fff;

  &__header {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    padding-bottom: 0.4rem;
    border-bottom: 1px dashed #e5e7eb;
  }

  &__header-line {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.7rem;
    font-weight: 600;
    color: #4d7c0f;

    i {
      font-size: 0.7rem;
    }

    &--secondary {
      color: #64748b;
      font-weight: 500;
    }
  }

  &__timeline {
    list-style: none;
    padding: 0.45rem 0 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
  }

  &__item {
    display: grid;
    grid-template-columns: 0.85rem 1fr auto;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.68rem;
    color: #94a3b8;

    &--done {
      color: #1e293b;
    }
  }

  &__bullet {
    font-size: 0.45rem;
    color: #cbd5e1;
    text-align: center;

    .flow-analytics-contact__item--done & {
      color: #4d7c0f;
    }
  }

  &__label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__count {
    color: #94a3b8;
    font-weight: 500;
    font-size: 0.62rem;
    margin-left: 0.2rem;
  }

  &__time {
    font-variant-numeric: tabular-nums;
    font-weight: 600;
    color: #475569;

    .flow-analytics-contact__item:not(.flow-analytics-contact__item--done) & {
      color: #cbd5e1;
      font-weight: 400;
    }
  }

  &__badge {
    margin-top: 0.4rem;
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.2rem 0.55rem;
    border-radius: 999px;
    background: #ecfccb;
    color: #4d7c0f;
    font-size: 0.68rem;
    font-weight: 600;

    i {
      font-size: 0.6rem;
    }

    // Variantes visuelles dérivées du `stepStatus` (cf. badgeVariant côté
    // script). On garde le vert par défaut (= success / passed).
    &--danger {
      background: #fee2e2;
      color: #b91c1c;
    }

    &--warning {
      background: #fef3c7;
      color: #92400e;
    }

    &--info {
      background: #dbeafe;
      color: #1d4ed8;
    }
  }

  // Le header-line reprend la même nuance que le badge pour rester cohérent.
  &--danger &__header-line {
    color: #b91c1c;
  }

  &--warning &__header-line {
    color: #92400e;
  }

  &--info &__header-line {
    color: #1d4ed8;
  }
}

.flow-analytics-tabs {
  display: flex;
  width: 100%;
}

.flow-analytics-tab {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.6875rem;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.15s ease;
  background: #f8fafc;
  color: #64748b;

  i {
    font-size: 0.625rem;
  }

  &--global {
    border-right: 1px solid #e2e8f0;

    &.flow-analytics-tab--active {
      background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
      color: #0369a1;
    }

    &:hover:not(.flow-analytics-tab--active) {
      background: #f1f5f9;
    }
  }

  &--send {
    &.flow-analytics-tab--active {
      background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
      color: #15803d;
    }

    &:hover:not(.flow-analytics-tab--active) {
      background: #f1f5f9;
    }
  }
}

.flow-analytics-content {
  padding: 0.5rem 0.75rem;
  background: #fff;
}

.flow-analytics-stats {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.flow-analytics-stat {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.6875rem;
  padding: 0.25rem 0;

  // Les lignes qui suivent portent sur des personnes déjà comptées plus haut :
  // un filet évite de les lire dans la continuité des motifs de blocage.
  &--new-group {
    border-top: 1px dashed #e2e8f0;
    margin-top: 0.15rem;
    padding-top: 0.4rem;
  }

  &__label {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    color: #475569;
    flex: 1;
    min-width: 0;

    i {
      color: #64748b;
      font-size: 0.625rem;
      width: 0.875rem;
      flex-shrink: 0;
    }
  }

  &__value {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    flex-shrink: 0;

    strong {
      font-weight: 600;
      font-size: 0.75rem;
      color: #1e293b;
    }
  }

  &__percent {
    color: #94a3b8;
    font-size: 0.625rem;
  }
}

.flow-analytics-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  width: 100%;
  padding: 0.375rem;
  margin-top: 0.25rem;
  font-size: 0.625rem;
  font-weight: 500;
  color: #0369a1;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    color: #0284c7;
    background: #f0f9ff;
    border-radius: 0.25rem;
  }

  i {
    font-size: 0.5rem;
  }
}

/* ── Lecture par famille de box ─────────────────────────────────────────── */
.flow-analytics-figures {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.6rem 0.75rem 0.7rem;
  background: #fff;
}

.flow-analytics-headline {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0.35rem;

  strong {
    font-size: 1.4rem;
    font-weight: 700;
    line-height: 1;
    letter-spacing: -0.02em;
    font-variant-numeric: tabular-nums;
    color: #1f2733;
  }

  span {
    font-size: 0.8125rem;
    color: #475569;
  }

  em {
    font-style: normal;
    font-size: 0.75rem;
    font-weight: 600;
    color: #1d4ed8;
    background: #dbeafe;
    padding: 0.05rem 0.4rem;
    border-radius: 999px;

    &.flow-analytics-rate--zero {
      color: #64748b;
      background: #e9eef5;
    }
  }
}

.flow-analytics-funnel {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.3rem;

  &__cell {
    display: flex;
    flex-direction: column;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 0.4rem;
    padding: 0.3rem 0.45rem;

    strong {
      font-size: 0.95rem;
      font-weight: 700;
      line-height: 1.15;
      font-variant-numeric: tabular-nums;
    }

    span {
      font-size: 0.6875rem;
      color: #7b8794;
    }

    small {
      font-size: 0.6875rem;
      font-weight: 600;
      color: #15803d;

      // Un taux nul n'est pas un résultat : le lire en vert ferait passer
      // « 0 % » pour un succès.
      &.flow-analytics-rate--zero {
        color: #94a3b8;
      }
    }
  }
}

.flow-analytics-revenue {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  background: #eef7dd;
  border-radius: 0.4rem;
  padding: 0.3rem 0.5rem;

  strong {
    font-size: 0.95rem;
    font-weight: 700;
    color: #4d7c0f;
    font-variant-numeric: tabular-nums;
  }

  span {
    font-size: 0.75rem;
    color: #475569;
  }
}

.flow-analytics-split {
  display: flex;
  gap: 2px;
  height: 8px;
  border-radius: 999px;
  overflow: hidden;
  background: #cbd5e1;

  &__part {
    display: block;
    height: 100%;
    min-width: 3px;
    flex-basis: 0;

    &--go { background: #22c55e; }
    &--wait { background: #a5b4c8; }
    &--neutral { background: #cbd5e1; }
    &--issue { background: #f87171; }
  }
}

// Une ligne ou un badge qui mène au support reste aligné sur ses voisins :
// on neutralise l'apparence de bouton et on n'ajoute que l'indice de clic.
.flow-analytics-stat--action,
.flow-analytics-contact__badge--action {
  border: 0;
  background: none;
  font: inherit;
  color: inherit;
  text-align: left;
  width: 100%;
  cursor: pointer;

  &:hover .flow-analytics-stat__label,
  &:hover span {
    text-decoration: underline;
  }
}

.flow-analytics-stat--action.flow-analytics-stat--new-group {
  border-top: 1px dashed #e2e8f0;
}

.flow-analytics-contact__badge--action {
  width: auto;
}

.flow-analytics-stat__dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 2px;
  margin-right: 0.35rem;
  vertical-align: baseline;

  &--go { background: #22c55e; }
  &--wait { background: #a5b4c8; }
  &--neutral { background: #cbd5e1; }
  &--issue { background: #f87171; }
}
</style>
